// Reine Aggregationslogik für die Auswertung einer Wartung (Kampagne).
// Rechnet aus Formularschema + Prüfantworten je Objekt: Status, Note, Mängel,
// Material und Antwortverteilungen pro Frage. Keine React-Abhängigkeiten.

import { LAYOUT_FIELD_TYPES, SEMANTIC_ROLES } from "@/lib/fieldTypeCatalog";
import type { CampaignEvaluationAsset, CampaignEvaluationResult } from "@/lib/wartungen";
import {
  coerceGradeValue,
  getSelectOptions,
  isValueEmpty,
  normalizeEnabledGrades,
  normalizeFields,
  type FieldOption,
  type NormalizedField,
} from "../form/runtimeModel";

/* ───────────────────────── Typen ───────────────────────── */

export type EvaluationStatusKey = "open" | "in_progress" | "ok" | "defect" | "follow_up" | "closed";
export type EvaluationStatusFilter = "all" | "done" | "issues" | "open";

export interface EvaluationQuestion {
  key: string;
  label: string;
  type: string;
  role: string;
  roleLabel: string;
  field: NormalizedField;
  options: FieldOption[];
  isDefect: boolean;
  isMaterial: boolean;
}

export interface DefectEntry {
  assetId: number;
  fieldKey: string;
  questionLabel: string;
  answerLabel: string;
  /** Note aus grade_effect der getroffenen Option bzw. des Triggers. */
  gradeEffect: number | null;
}

export interface MaterialEntry {
  assetId: number;
  fieldKey: string;
  questionLabel: string;
  /** Gruppierungsschlüssel (Artikel-ID, Hero-Nr. oder Bezeichnung). */
  groupKey: string;
  label: string;
  heroNr: string;
  artikelId: number | null;
  qty: number | null;
  unit: string;
}

export interface MaterialAggregate {
  groupKey: string;
  label: string;
  heroNr: string;
  artikelId: number | null;
  unit: string;
  /** Summe der erfassten Mengen (nur numerische `qty`). */
  qtySum: number;
  /** Anzahl Erfassungen ohne Mengenangabe. */
  countWithoutQty: number;
  /** Anzahl Erfassungen insgesamt. */
  entries: number;
  assetIds: number[];
}

export interface DefectGroup {
  fieldKey: string;
  questionLabel: string;
  entries: DefectEntry[];
  /** Verteilung der Antworten innerhalb dieser Mangel-Frage. */
  answers: { label: string; count: number; gradeEffect: number | null }[];
}

export interface AssetEvaluationRow {
  asset: CampaignEvaluationAsset;
  assetId: number;
  label: string;
  externalId: string;
  name: string;
  subLocationName: string;
  status: EvaluationStatusKey;
  isDone: boolean;
  hasIssue: boolean;
  grade: number | null;
  gradeAuto: number | null;
  gradeManual: number | null;
  completedAt: string | null;
  performedBy: string;
  defects: DefectEntry[];
  materials: MaterialEntry[];
  /** Formatierte Antworten je Frage-Key (nur ausgewählte Fragen). */
  answers: Record<string, string>;
}

export interface QuestionOptionStat {
  value: string;
  label: string;
  count: number;
  assetIds: number[];
  isDefect: boolean;
  gradeEffect: number | null;
}

export interface QuestionBreakdown {
  question: EvaluationQuestion;
  kind: "options" | "bool" | "number" | "text" | "material";
  answered: number;
  unanswered: number;
  options: QuestionOptionStat[];
  numeric: { min: number; max: number; avg: number; sum: number } | null;
  textAnswers: { assetId: number; text: string }[];
}

export interface EvaluationSummary {
  totalAssets: number;
  done: number;
  inProgress: number;
  open: number;
  withIssues: number;
  defectEntries: number;
  materialEntries: number;
  gradedAssets: number;
  averageGrade: number | null;
  worstGrade: number | null;
  percentDone: number;
}

export interface GradeBucket {
  grade: number;
  count: number;
  percent: number;
}

export interface EvaluationModel {
  questions: EvaluationQuestion[];
  selectedQuestions: EvaluationQuestion[];
  enabledGrades: number[];
  rows: AssetEvaluationRow[];
  summary: EvaluationSummary;
  gradeBuckets: GradeBucket[];
  ungradedDone: number;
  defectGroups: DefectGroup[];
  materialAggregates: MaterialAggregate[];
  questionBreakdowns: QuestionBreakdown[];
  subLocations: { id: number | null; name: string; count: number }[];
}

/* ───────────────────────── Fragen ───────────────────────── */

const BINARY_FIELD_TYPES = new Set(["signature", "file", "photo", "pdf-upload"]);
const MATERIAL_FIELD_TYPES = new Set(["article_ref", "article_ref_list", "material_link"]);
const ROLE_LABELS = new Map<string, string>(SEMANTIC_ROLES.map((r) => [r.value, r.label]));

export function questionRole(field: NormalizedField): string {
  const raw =
    (field as { effective_semantic_role?: unknown }).effective_semantic_role ??
    (field as { semantic_role_override?: unknown }).semantic_role_override ??
    (field as { semantic_role?: unknown }).semantic_role ??
    "";
  return String(raw ?? "").trim().toLowerCase();
}

/**
 * Auswertbare Inspektionsfragen aus dem Formularschema (ohne Layout-,
 * Signatur- und Datei-Felder). Reihenfolge = Formularreihenfolge.
 */
export function buildEvaluationQuestions(result: CampaignEvaluationResult | null | undefined): EvaluationQuestion[] {
  const fields = normalizeFields({ fields: result?.form?.fields ?? [] });
  const questions: EvaluationQuestion[] = [];
  for (const field of fields) {
    if (field.effective_scope !== "inspection") continue;
    const type = field.effective_field_type;
    if (LAYOUT_FIELD_TYPES.has(type) || BINARY_FIELD_TYPES.has(type)) continue;
    if (type === "linked_asset") continue;
    const role = questionRole(field);
    questions.push({
      key: field.field_key,
      label: field.effective_label || field.field_key,
      type,
      role,
      roleLabel: ROLE_LABELS.get(role) ?? "",
      field,
      options: getSelectOptions(field),
      isDefect: role === "defect",
      isMaterial: role === "material" || MATERIAL_FIELD_TYPES.has(type),
    });
  }
  return questions;
}

/** Standard-Auswahl: alle auswertbaren Fragen in Formularreihenfolge. */
export function defaultSelectedQuestionKeys(questions: EvaluationQuestion[]): string[] {
  return questions.map((q) => q.key);
}

/** Gespeicherte Auswahl gegen aktuelles Schema bereinigen (Reihenfolge der Auswahl bleibt). */
export function normalizeSelectedQuestionKeys(saved: string[] | null | undefined, questions: EvaluationQuestion[]): string[] {
  if (!Array.isArray(saved)) return defaultSelectedQuestionKeys(questions);
  const available = new Set(questions.map((q) => q.key));
  const cleaned = saved.map((k) => String(k).trim()).filter((k) => k && available.has(k));
  return Array.from(new Set(cleaned));
}

/* ───────────────────────── Werte formatieren ───────────────────────── */

const DATE_TIME_FORMAT = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });
const DATE_FORMAT = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium" });
const NUMBER_FORMAT = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "–";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : DATE_TIME_FORMAT.format(d);
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "–";
  return NUMBER_FORMAT.format(value);
}

function optionLabelFor(question: EvaluationQuestion, raw: unknown): string {
  const v = String(raw ?? "").trim();
  const match = question.options.find((o) => o.value === v);
  return match?.label || v;
}

function isMaterialValue(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const r = value as Record<string, unknown>;
  return "artikel_id" in r || "article_id" in r || "hero_nr" in r || "label" in r || "qty" in r;
}

function formatMaterialValue(value: Record<string, unknown>): string {
  const label = String(value.label ?? value.name ?? value.bezeichnung ?? "").trim();
  const heroNr = String(value.hero_nr ?? "").trim();
  const qty = value.qty != null && value.qty !== "" ? Number(value.qty) : null;
  const unit = String(value.unit ?? "").trim();
  const head = [heroNr, label].filter(Boolean).join(" – ") || String(value.artikel_id ?? value.article_id ?? "");
  const amount = qty != null && Number.isFinite(qty) ? `${formatNumber(qty)}${unit ? ` ${unit}` : ""}` : "";
  return [head, amount].filter(Boolean).join(" · ");
}

/** Lesbare Antwort einer Frage (Optionslabels, Ja/Nein, Material, Datum …). */
export function formatAnswer(question: EvaluationQuestion, value: unknown): string {
  if (isValueEmpty(value)) return "";
  if (value && typeof value === "object" && !Array.isArray(value) && (value as { __binary?: boolean }).__binary) {
    return "Datei vorhanden";
  }
  switch (question.type) {
    case "checkbox":
      return value === true || value === "true" || value === 1 || value === "1" ? "Ja" : "Nein";
    case "date": {
      const d = new Date(String(value));
      return Number.isNaN(d.getTime()) ? String(value) : DATE_FORMAT.format(d);
    }
    case "datetime":
      return formatDateTime(String(value));
    case "number": {
      const n = Number(value);
      return Number.isFinite(n) ? formatNumber(n) : String(value);
    }
    default:
      break;
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => (isMaterialValue(entry) ? formatMaterialValue(entry) : optionLabelFor(question, entry)))
      .filter(Boolean)
      .join(", ");
  }
  if (isMaterialValue(value)) return formatMaterialValue(value);
  if (typeof value === "object") {
    const r = value as Record<string, unknown>;
    return String(r.label ?? r.name ?? r.external_id ?? JSON.stringify(r));
  }
  return optionLabelFor(question, value);
}

/* ───────────────────────── Status & Note ───────────────────────── */

export function resolveEvaluationStatus(asset: CampaignEvaluationAsset): EvaluationStatusKey {
  const event = asset.event;
  if (!event) return "open";
  if (event.is_completed) {
    const result = String(event.result_status ?? "").trim().toLowerCase();
    if (result === "ok") return "ok";
    if (result === "defect_found") return "defect";
    if (result === "follow_up_required") return "follow_up";
    return "closed";
  }
  return "in_progress";
}

export const EVALUATION_STATUS_LABELS: Record<EvaluationStatusKey, string> = {
  open: "Offen",
  in_progress: "In Bearbeitung",
  ok: "In Ordnung",
  defect: "Mangel",
  follow_up: "Nacharbeit",
  closed: "Abgeschlossen",
};

export function statusIsDone(status: EvaluationStatusKey): boolean {
  return status === "ok" || status === "defect" || status === "follow_up" || status === "closed";
}

export function statusHasIssue(status: EvaluationStatusKey): boolean {
  return status === "defect" || status === "follow_up";
}

function resolveGrade(asset: CampaignEvaluationAsset): { final: number | null; auto: number | null; manual: number | null } {
  const event = asset.event;
  if (!event) return { final: null, auto: null, manual: null };
  const data = event.data ?? {};
  const final = coerceGradeValue(event.grade_final ?? data.maintenance_grade_final ?? data.maintenance_grade);
  const auto = coerceGradeValue(event.grade_auto ?? data.maintenance_grade_auto);
  const manual = coerceGradeValue(event.grade_manual ?? data.maintenance_grade_manual);
  return { final: final ?? manual ?? auto, auto, manual };
}

/* ───────────────────────── Mängel ───────────────────────── */

interface DefectTrigger {
  mode: string;
  values: string[];
  gradeEffect: number | null;
}

function normalizeAnswerValues(value: unknown): string[] {
  const norm = (raw: unknown): string => {
    if (typeof raw === "string") return raw.trim().toLowerCase();
    if (typeof raw === "boolean") return raw ? "true" : "false";
    if (typeof raw === "number") return String(raw);
    return "";
  };
  const list = Array.isArray(value) ? value.map(norm) : [norm(value)];
  return list.filter(Boolean);
}

function resolveDefectTrigger(question: EvaluationQuestion): DefectTrigger | null {
  const raw = (question.field as { defect_trigger?: unknown }).defect_trigger;
  if (raw && typeof raw === "object") {
    const r = raw as { mode?: unknown; values?: unknown; grade_effect?: unknown };
    const values = Array.isArray(r.values) ? r.values.map((v) => String(v).trim().toLowerCase()).filter(Boolean) : [];
    if (values.length) {
      return {
        mode: String(r.mode ?? "in").trim().toLowerCase() || "in",
        values,
        gradeEffect: coerceGradeValue(r.grade_effect),
      };
    }
  }
  const defectValues = question.options.filter((o) => o.is_defect).map((o) => o.value.trim().toLowerCase());
  if (defectValues.length) return { mode: "in", values: defectValues, gradeEffect: null };
  return null;
}

function storedDefectIndex(data: Record<string, unknown> | undefined): Map<string, boolean> | null {
  const raw = data?.__defect_index;
  if (!raw || typeof raw !== "object") return null;
  const fields = (raw as { defect_fields?: unknown }).defect_fields;
  if (!Array.isArray(fields)) return null;
  const map = new Map<string, boolean>();
  for (const entry of fields) {
    if (!entry || typeof entry !== "object") continue;
    const key = String((entry as { field_key?: unknown }).field_key ?? "").trim();
    if (key) map.set(key, Boolean((entry as { triggered?: unknown }).triggered));
  }
  return map;
}

function collectDefects(asset: CampaignEvaluationAsset, questions: EvaluationQuestion[]): DefectEntry[] {
  const event = asset.event;
  if (!event?.is_completed) return [];
  const data = event.data ?? {};
  const stored = storedDefectIndex(data);
  const result: DefectEntry[] = [];

  for (const question of questions) {
    if (!question.isDefect) continue;
    const value = data[question.key];
    if (isValueEmpty(value)) continue;
    const trigger = resolveDefectTrigger(question);
    const answers = normalizeAnswerValues(value);

    let triggered: boolean;
    let matched: string[] = [];
    if (trigger) {
      const triggerSet = new Set(trigger.values);
      matched = answers.filter((a) => triggerSet.has(a));
      triggered = trigger.mode === "equals" ? answers.length === 1 && matched.length > 0 : matched.length > 0;
    } else {
      // Freitext-/Zahlfeld mit Rolle „Mangel“: Eintrag zählt als Mangelbeschreibung.
      triggered = question.options.length === 0;
    }
    if (stored?.has(question.key)) triggered = stored.get(question.key) === true;
    if (!triggered) continue;

    let gradeEffect: number | null = null;
    for (const opt of question.options) {
      if (matched.includes(opt.value.trim().toLowerCase()) && opt.grade_effect != null) {
        gradeEffect = gradeEffect == null ? opt.grade_effect : Math.max(gradeEffect, opt.grade_effect);
      }
    }
    if (gradeEffect == null) gradeEffect = trigger?.gradeEffect ?? null;

    result.push({
      assetId: asset.asset_id,
      fieldKey: question.key,
      questionLabel: question.label,
      answerLabel: formatAnswer(question, value) || "Mangel",
      gradeEffect,
    });
  }
  return result;
}

/* ───────────────────────── Material ───────────────────────── */

function toMaterialEntry(asset: CampaignEvaluationAsset, question: EvaluationQuestion, raw: unknown): MaterialEntry | null {
  let label = "";
  let heroNr = "";
  let artikelId: number | null = null;
  let qty: number | null = null;
  let unit = "";
  if (isMaterialValue(raw)) {
    label = String(raw.label ?? raw.name ?? raw.bezeichnung ?? "").trim();
    heroNr = String(raw.hero_nr ?? "").trim();
    const idRaw = Number(raw.artikel_id ?? raw.article_id);
    artikelId = Number.isFinite(idRaw) && idRaw > 0 ? idRaw : null;
    const qtyRaw = raw.qty != null && raw.qty !== "" ? Number(raw.qty) : NaN;
    qty = Number.isFinite(qtyRaw) ? qtyRaw : null;
    unit = String(raw.unit ?? "").trim();
  } else if (typeof raw === "string" || typeof raw === "number") {
    label = optionLabelFor(question, raw);
  } else {
    return null;
  }
  if (!label && !heroNr && artikelId == null) return null;
  const groupKey = artikelId != null ? `id:${artikelId}` : heroNr ? `nr:${heroNr.toLowerCase()}` : `label:${label.toLowerCase()}`;
  return {
    assetId: asset.asset_id,
    fieldKey: question.key,
    questionLabel: question.label,
    groupKey,
    label: label || heroNr || `Artikel #${artikelId}`,
    heroNr,
    artikelId,
    qty,
    unit,
  };
}

function collectMaterials(asset: CampaignEvaluationAsset, questions: EvaluationQuestion[]): MaterialEntry[] {
  const event = asset.event;
  if (!event) return [];
  const data = event.data ?? {};
  const result: MaterialEntry[] = [];
  for (const question of questions) {
    if (!question.isMaterial) continue;
    const value = data[question.key];
    if (isValueEmpty(value)) continue;
    const list = Array.isArray(value) ? value : [value];
    for (const raw of list) {
      const entry = toMaterialEntry(asset, question, raw);
      if (entry) result.push(entry);
    }
  }
  return result;
}

export function aggregateMaterials(entries: MaterialEntry[]): MaterialAggregate[] {
  const map = new Map<string, MaterialAggregate>();
  for (const entry of entries) {
    let agg = map.get(entry.groupKey);
    if (!agg) {
      agg = {
        groupKey: entry.groupKey,
        label: entry.label,
        heroNr: entry.heroNr,
        artikelId: entry.artikelId,
        unit: entry.unit,
        qtySum: 0,
        countWithoutQty: 0,
        entries: 0,
        assetIds: [],
      };
      map.set(entry.groupKey, agg);
    }
    agg.entries += 1;
    if (entry.qty != null) agg.qtySum += entry.qty;
    else agg.countWithoutQty += 1;
    if (!agg.unit && entry.unit) agg.unit = entry.unit;
    if (!agg.assetIds.includes(entry.assetId)) agg.assetIds.push(entry.assetId);
  }
  return Array.from(map.values()).sort((a, b) => b.entries - a.entries || a.label.localeCompare(b.label, "de"));
}

/* ───────────────────────── Fragen-Verteilung ───────────────────────── */

function buildQuestionBreakdown(question: EvaluationQuestion, rows: AssetEvaluationRow[]): QuestionBreakdown {
  const doneRows = rows.filter((r) => r.isDone && r.asset.event);
  let answered = 0;
  const optionStats = new Map<string, QuestionOptionStat>();
  const numericValues: number[] = [];
  const textAnswers: { assetId: number; text: string }[] = [];

  let kind: QuestionBreakdown["kind"] = "text";
  if (question.isMaterial) kind = "material";
  else if (question.type === "checkbox") kind = "bool";
  else if (question.type === "number") kind = "number";
  else if (question.options.length) kind = "options";

  if (kind === "options") {
    for (const opt of question.options) {
      optionStats.set(opt.value, {
        value: opt.value,
        label: opt.label,
        count: 0,
        assetIds: [],
        isDefect: Boolean(opt.is_defect) || (opt.grade_effect != null && opt.grade_effect >= 3),
        gradeEffect: opt.grade_effect ?? null,
      });
    }
  } else if (kind === "bool") {
    optionStats.set("true", { value: "true", label: "Ja", count: 0, assetIds: [], isDefect: false, gradeEffect: null });
    optionStats.set("false", { value: "false", label: "Nein", count: 0, assetIds: [], isDefect: false, gradeEffect: null });
  }

  for (const row of doneRows) {
    const value = row.asset.event?.data?.[question.key];
    if (isValueEmpty(value)) continue;
    answered += 1;
    if (kind === "options") {
      const list = Array.isArray(value) ? value : [value];
      for (const raw of list) {
        const v = String(raw ?? "").trim();
        let stat = optionStats.get(v);
        if (!stat) {
          stat = { value: v, label: v, count: 0, assetIds: [], isDefect: false, gradeEffect: null };
          optionStats.set(v, stat);
        }
        stat.count += 1;
        stat.assetIds.push(row.assetId);
      }
    } else if (kind === "bool") {
      const key = value === true || value === "true" || value === 1 || value === "1" ? "true" : "false";
      const stat = optionStats.get(key)!;
      stat.count += 1;
      stat.assetIds.push(row.assetId);
    } else if (kind === "number") {
      const n = Number(value);
      if (Number.isFinite(n)) numericValues.push(n);
    } else if (kind === "material") {
      textAnswers.push({ assetId: row.assetId, text: formatAnswer(question, value) });
    } else {
      textAnswers.push({ assetId: row.assetId, text: formatAnswer(question, value) });
    }
  }

  let numeric: QuestionBreakdown["numeric"] = null;
  if (numericValues.length) {
    const sum = numericValues.reduce((a, b) => a + b, 0);
    numeric = {
      min: Math.min(...numericValues),
      max: Math.max(...numericValues),
      avg: sum / numericValues.length,
      sum,
    };
  }

  return {
    question,
    kind,
    answered,
    unanswered: Math.max(0, doneRows.length - answered),
    options: Array.from(optionStats.values()),
    numeric,
    textAnswers,
  };
}

/* ───────────────────────── Hauptmodell ───────────────────────── */

export interface BuildEvaluationOptions {
  selectedQuestionKeys: string[];
  /** Bereichs-Filter: Unterort-ID, `"none"` für Objekte ohne Bereich, `null` = alle. */
  subLocationId?: number | "none" | null;
  statusFilter?: EvaluationStatusFilter;
  textFilter?: string;
}

export function assetDisplayLabel(asset: CampaignEvaluationAsset): string {
  const ext = String(asset.external_id ?? "").trim();
  const name = String(asset.asset_name ?? "").trim();
  if (ext && name && ext !== name) return `${ext} · ${name}`;
  return ext || name || `#${asset.asset_id}`;
}

export function buildEvaluationModel(
  result: CampaignEvaluationResult | null | undefined,
  options: BuildEvaluationOptions,
): EvaluationModel {
  const questions = buildEvaluationQuestions(result);
  const selectedKeys = normalizeSelectedQuestionKeys(options.selectedQuestionKeys, questions);
  const selectedSet = new Set(selectedKeys);
  const selectedQuestions = selectedKeys
    .map((key) => questions.find((q) => q.key === key))
    .filter((q): q is EvaluationQuestion => Boolean(q));
  const enabledGrades = normalizeEnabledGrades(result?.form?.grade_config_json ?? null);

  const allAssets = Array.isArray(result?.assets) ? result!.assets! : [];

  // Bereiche (Unterorte) aus allen Objekten – unabhängig vom Filter
  const subLocationMap = new Map<string, { id: number | null; name: string; count: number }>();
  for (const asset of allAssets) {
    const id = asset.sub_location_id ?? null;
    const key = id == null ? "none" : String(id);
    const existing = subLocationMap.get(key);
    if (existing) existing.count += 1;
    else subLocationMap.set(key, { id, name: String(asset.sub_location_name ?? "").trim() || "Ohne Bereich", count: 1 });
  }
  const subLocations = Array.from(subLocationMap.values()).sort((a, b) => a.name.localeCompare(b.name, "de", { numeric: true }));

  const tf = String(options.textFilter ?? "").trim().toLowerCase();
  const scopedAssets = allAssets.filter((asset) => {
    if (options.subLocationId === "none") {
      if (asset.sub_location_id != null) return false;
    } else if (options.subLocationId != null && Number(asset.sub_location_id) !== Number(options.subLocationId)) {
      return false;
    }
    if (tf && !`${asset.external_id ?? ""} ${asset.asset_name ?? ""}`.toLowerCase().includes(tf)) return false;
    return true;
  });

  const rows: AssetEvaluationRow[] = scopedAssets.map((asset) => {
    const status = resolveEvaluationStatus(asset);
    const grades = resolveGrade(asset);
    const defects = collectDefects(asset, questions.filter((q) => selectedSet.has(q.key)));
    const materials = collectMaterials(asset, questions.filter((q) => selectedSet.has(q.key)));
    const answers: Record<string, string> = {};
    if (asset.event) {
      for (const question of selectedQuestions) {
        answers[question.key] = formatAnswer(question, asset.event.data?.[question.key]);
      }
    }
    return {
      asset,
      assetId: asset.asset_id,
      label: assetDisplayLabel(asset),
      externalId: String(asset.external_id ?? "").trim(),
      name: String(asset.asset_name ?? "").trim(),
      subLocationName: String(asset.sub_location_name ?? "").trim(),
      status,
      isDone: statusIsDone(status),
      hasIssue: statusHasIssue(status) || defects.length > 0,
      grade: grades.final,
      gradeAuto: grades.auto,
      gradeManual: grades.manual,
      completedAt: asset.event?.completed_at ?? asset.event?.updated_at ?? null,
      performedBy: String(asset.event?.performed_by_display_name ?? asset.event?.performed_by_user_id ?? "").trim(),
      defects,
      materials,
      answers,
    };
  });

  const filteredRows = rows.filter((row) => {
    switch (options.statusFilter ?? "all") {
      case "done":
        return row.isDone;
      case "issues":
        return row.hasIssue;
      case "open":
        return !row.isDone;
      default:
        return true;
    }
  });

  // Kennzahlen beziehen sich auf den Bereichs-/Text-Scope (nicht auf den Statusfilter),
  // damit „X von Y gewartet“ stabil bleibt, während man die Liste einschränkt.
  const done = rows.filter((r) => r.isDone);
  const gradedRows = done.filter((r) => r.grade != null);
  const gradeSum = gradedRows.reduce((acc, r) => acc + (r.grade ?? 0), 0);
  const allDefects = rows.flatMap((r) => r.defects);
  const allMaterials = rows.flatMap((r) => r.materials);

  const summary: EvaluationSummary = {
    totalAssets: rows.length,
    done: done.length,
    inProgress: rows.filter((r) => r.status === "in_progress").length,
    open: rows.filter((r) => r.status === "open").length,
    withIssues: rows.filter((r) => r.hasIssue).length,
    defectEntries: allDefects.length,
    materialEntries: allMaterials.length,
    gradedAssets: gradedRows.length,
    averageGrade: gradedRows.length ? gradeSum / gradedRows.length : null,
    worstGrade: gradedRows.length ? Math.max(...gradedRows.map((r) => r.grade ?? 0)) : null,
    percentDone: rows.length ? Math.round((done.length / rows.length) * 100) : 0,
  };

  const gradeCounts = new Map<number, number>();
  for (const r of gradedRows) gradeCounts.set(r.grade!, (gradeCounts.get(r.grade!) ?? 0) + 1);
  const gradeValues = Array.from(new Set([...enabledGrades, ...gradeCounts.keys()])).sort((a, b) => a - b);
  const gradeBuckets: GradeBucket[] = gradeValues.map((grade) => {
    const count = gradeCounts.get(grade) ?? 0;
    return { grade, count, percent: gradedRows.length ? Math.round((count / gradedRows.length) * 100) : 0 };
  });

  const defectGroupMap = new Map<string, DefectGroup>();
  for (const entry of allDefects) {
    let group = defectGroupMap.get(entry.fieldKey);
    if (!group) {
      group = { fieldKey: entry.fieldKey, questionLabel: entry.questionLabel, entries: [], answers: [] };
      defectGroupMap.set(entry.fieldKey, group);
    }
    group.entries.push(entry);
    const answer = group.answers.find((a) => a.label === entry.answerLabel);
    if (answer) answer.count += 1;
    else group.answers.push({ label: entry.answerLabel, count: 1, gradeEffect: entry.gradeEffect });
  }
  const defectGroups = Array.from(defectGroupMap.values())
    .map((g) => ({ ...g, answers: g.answers.sort((a, b) => b.count - a.count) }))
    .sort((a, b) => b.entries.length - a.entries.length);

  const questionBreakdowns = selectedQuestions.map((q) => buildQuestionBreakdown(q, rows));

  return {
    questions,
    selectedQuestions,
    enabledGrades,
    rows: filteredRows,
    summary,
    gradeBuckets,
    ungradedDone: done.length - gradedRows.length,
    defectGroups,
    materialAggregates: aggregateMaterials(allMaterials),
    questionBreakdowns,
    subLocations,
  };
}
